/**
 * The coach: nineteen mechanical readings of one graph, after a run has stopped.
 *
 * DERIVED, NEVER FROZEN. The set is recomputed from the document every time, so
 * acting on one cue honestly changes the rest — a claim that gets a source check
 * stops being the thinnest load-bearing one, and says so on the next pass. What
 * survives a recompute is the retirement state the caller holds: `key` identifies
 * an INSTANCE and `kind` alone identifies what may be muted. A key is
 * `kind:anchorId`, or `kind:anchorId:record` where the cue is about an
 * identifiable record — see `Cue.key`.
 *
 * This is EDITORIAL ORDER, NOT A VERDICT. `CUE_RISK_RANK` and `CUE_EXP_RANK` are
 * hand-written integer tables — the `pressureOf` precedent — and they say which
 * cue this app would read first, never which claim is truer. Nothing here scores
 * a node.
 *
 * SELF-RATINGS ARE COMPARED ONLY WITHIN ONE MODEL STAMP, AND ONLY WHERE THEY ARE
 * STILL THE MODEL'S. Four of the nineteen (`thin`, `unchecked`, `frontier`,
 * `strong`) rank on `confidence`, which is each model's own scale and not a
 * probability. They are therefore confined to the largest single-model cohort in
 * the pool, they drop nodes the user has rewritten (`modelRated`), they refuse to
 * fire on a cohort too small to be a comparison at all (`COHORT_MIN`), and the
 * metric line names the model whose scale is being read. Everything else here
 * reads graph shape and records that already exist.
 *
 * NOTHING HERE CALLS A MODEL, WRITES STATE, OR CAN END OR EXTEND A RUN. A cue is
 * DATA — its action is a descriptor a person presses, never a closure — so the
 * whole set is JSON-serializable and a cue cannot spend by itself. The layer runs
 * after the stop condition and never touches it (invariant 6).
 *
 * It imports `./types`, `./panel`, `./endings`, `./anomaly` — itself a leaf over
 * the first two — and the status words, and one reading from outside the graph's
 * own records, deliberately few: the store imports this module, so nothing it
 * imports may reach the store. `./endings` has no runtime imports at all. The one
 * reading is the Lab's evidence genealogy (`./claimEvidence`, `./genealogy`), which
 * `onestudy` asks about one claim's reports: it reaches `researchProjection.ts` and
 * what that builds on, every module of which the store already loads and none of
 * which loads the store — checked, not assumed, when the cue arrived. The helpers
 * it needs from the retired First Read planner are absorbed here rather than
 * imported.
 */

import { anomalyRecords, mixedShape, type AnomalyKind, type AnomalyRecord } from './anomaly'
import { claimRecordIds, reportBound, sessionEvidence } from './claimEvidence'
import { ENDING_LABEL, ENDING_ORDER, endingOf } from './endings'
import type { Ending } from './endings'
import {
  independenceTally,
  kinClusters,
  type IndependenceTally,
  type KinCluster,
} from './genealogy'
import { panelSplit, tallyPanel } from './panel'
import { SYNDICATION_HAMMING_MAX, fnv1a64 } from './similarity'
import { standingOn, taintMap } from './taint'
import { writtenAt } from './rewrite'
import { PANEL_CHIP_LABEL, PANEL_VERDICT_LABEL, STATUS_WORD } from './vocabulary'
import { calendarDay } from './dates'
import {
  auditLive,
  bridgeReadable,
  channelOf,
  checkedAgainstSources,
  localEnds,
  outcomeLive,
  retractedWorks,
  spendKey,
  verificationLive,
  type Bridge,
  type Channel,
  type ModelStamp,
  type ReasonNode,
  type Run,
  type Session,
} from './types'
import { BRIDGE_RUNGS_TOTAL, bridgeStanding, sortBridges } from './rung'

export type CueKind =
  | 'world'
  | 'retracted'
  | 'stale'
  | 'lenses'
  | 'contra'
  | 'blast'
  | 'flat'
  | 'thin'
  | 'unchecked'
  | 'onemodel'
  | 'test'
  | 'decide'
  | 'join'
  | 'frontier'
  | 'strong'
  | 'prune'
  | 'onestudy'
  | 'unexplained'
  | 'unsearched'

/**
 * Every action a cue can carry. Each one names a store or engine call that
 * happens when pressed — a cue whose button is a toast pretending to be an
 * operation fails this layer's own standard.
 *
 * EXACTLY THE KINDS SOME CUE USES, and it stays that way. `panel` and
 * `experiment` were carried here from the retired First Read planner with no cue
 * behind either, which meant the price tables below and the dispatch in
 * `ui/Coach.tsx` all held arms nothing could reach — unreachable code that reads
 * as covered behaviour and is not. A future cue that wants one adds its kind back
 * TOGETHER WITH the `CUE_ACTION` row that presses it, so the tables can never
 * again describe presses that do not exist.
 *
 * There is no `replay`. The design sheet gave `flat` a button that seeks the
 * transport to the stalled stretch, but stalled moves are by definition moves
 * that produced no nodes: there is nothing on the canvas for the playhead to
 * scrub to, so the press would move a slider and show the same board. The empty
 * move rows exist in exactly one place — the run account — and that is where
 * `flat` sends you. A button must name what actually happens.
 */
export type CueActionKind =
  | 'verify'
  | 'deepen'
  | 'challenge'
  | 'record'
  | 'inspect'
  | 'audit'
  | 'linkScan'
  | 'report'
  | 'prune'
  | 'checkResting'
  | 'group'
  | 'conjecture'
  | 'priorArt'

/**
 * How many claims `checkResting` names: the ones most of the board rests on, at
 * most this many, one source check each, as one checks-only run.
 *
 * Three, because it is the size of a list a reader takes in at a glance and can
 * check against the graph by hand, and because each claim on it is a whole check's
 * allowance of live requests — a press that spent ten would be a run nobody sized.
 * Exported beside the thresholds for the same reason they are: the button's words
 * are built from it, so the number cannot move without the label moving with it.
 */
export const RESTING_CHECKS = 3

export interface CueAction {
  kind: CueActionKind
  label: string
}

/**
 * The verb each action prices through, or null for the ones that spend nothing.
 *
 * One table, because the CHEAPEST lens and the figure printed on the button have
 * to be the same arithmetic — a board sorted by one estimate and labelled with
 * another is two prices for one press. The verb names are a subset of what
 * `VerbPrice` accepts, which is what `projectVerbUsd` is asked for at the call
 * site; the `panel` verb is missing because no cue's action is a panel, and a
 * table row for a press nobody can make is a claim about behaviour that does not
 * exist.
 *
 * `linkScan` is null deliberately. A link scan does spend, but no figure for it
 * goes through the one component that prints prices here, and the house rule for
 * a missing list rate is the same either way: no figure beats a wrong one.
 *
 * `priorArt` is a verb of its own rather than the check's shape reused, and that
 * was a decision (D281): the search runs on the check role but with its own,
 * larger allowance of live requests, so pricing it as a source check would print
 * a figure under what the press can draw — the one direction a price here may not
 * be wrong in. `VerbPrice` and `projectVerbUsd` already carry it.
 */
export const CUE_PRICE_VERB: Record<
  CueActionKind,
  'move' | 'verify' | 'priorArt' | 'audit' | null
> = {
  verify: 'verify', // a source check, at the check role's rates plus its searches
  deepen: 'move', // one reasoning pass
  challenge: 'move', // one reasoning pass
  conjecture: 'move', // one reasoning pass, on the reasoning role
  priorArt: 'priorArt', // the check role's rates plus the search's own allowance
  audit: 'audit', // another house's rates, resolved from the auditor
  record: null, // a form the user fills in; no model runs
  inspect: null, // a selection
  report: null, // a tab
  prune: null, // a local edit, undoable
  linkScan: null, // spends, but carries no figure here — see above
  // Spends, and HAS a figure, but not a verb's: it is a checks-only run, priced by the
  // run projection over as many checks as it names. `ui/restingPress.ts` prints that
  // figure on the button and hands the same number to the CHEAPEST lens, so the one-
  // arithmetic rule holds through a different door.
  checkResting: null,
  group: null, // human overrides in the Lab's ledger; no model runs
}

/**
 * Which actions spend, regardless of whether a figure is available. NOT
 * derivable from `CUE_PRICE_VERB`: a null there means "no projection", and that
 * covers two opposite facts — `record` costs nothing, while `linkScan` runs a
 * real paid pass whose figure this table simply cannot carry. The CHEAPEST lens
 * needs the distinction, because a paid action with no figure is unknown rather
 * than free, and unknown sorts last, never first.
 */
export const CUE_ACTION_PAID: Record<CueActionKind, boolean> = {
  verify: true,
  deepen: true,
  challenge: true,
  audit: true,
  linkScan: true,
  conjecture: true,
  priorArt: true,
  record: false,
  inspect: false,
  report: false,
  prune: false,
  checkResting: true,
  group: false,
}

export interface Cue {
  /**
   * The retirement identity, stable across recomputes: `kind:anchorId`, or
   * `kind:anchorId:record` where the cue is about an identifiable record.
   *
   * The third segment is not decoration. Six kinds speak for a specific record
   * on the anchor — the bridge `join` proposes, the panel `lenses` read, the
   * outcome `world` reports, the run `flat` counted, the newest record under a
   * `stale` number, the source check a `retracted` work was cited by — and with
   * the anchor alone as the identity, answering one of them silences the NEXT one
   * on the same node: a second bridge off the same claim, the panel you just
   * re-ran, the check you just re-ran, would arrive already retired. So the key
   * names the record, and a new record is honestly a new instance. Kinds
   * with no such record keep the two-segment form, because there is nothing to
   * name and `kind:anchorId` is already the whole story.
   */
  key: string
  kind: CueKind
  anchorId: string
  /** Secondary nodes the cue is about: the objection, the newer record, the far
   *  end of a join. Never a second anchor — the camera walks to one node. */
  alsoIds: string[]
  channel: Channel
  /** The anchor node's label, as written. */
  title: string
  /** One to three sentences. What the record says, and nothing beyond it. */
  body: string
  /** ALL-CAPS mono facts. Counts and stored numbers only, never a price. */
  metric: string
  /** The WHY line: the condition that fired, with the thresholds it used. */
  rule: string
  riskRank: number
  expRank: number
  action?: CueAction
}

/**
 * What the document cannot say about itself: which run just finished, what the
 * user is focused on, and the two budget figures the `decide` gate reads.
 *
 * The dollar fields are read as an inequality and never printed. This module
 * emits no figure and no currency word — the price on a button is the UI's job,
 * through the one arithmetic every other price in the app goes through.
 */
export interface CueContext {
  run?: Run | null
  focusId?: string | null
  capLeftUsd?: number | null
  nextMoveUsd?: number | null
}

/**
 * Retirement state, held by the caller and applied here.
 *
 * `seen` is keyed by cue key (per instance) and `skips`/`muted` by kind, which is
 * the whole point of two identities: acting on one `thin` must not silence the
 * next one.
 */
export interface CueFilters {
  seen: Record<string, unknown>
  skips: Record<string, number>
  muted: Record<string, unknown>
}

export type CueLens = 'risk' | 'cheapest' | 'experiment'

/** The kind headline. One map, because the card, the pin and the digest row all
 *  print it — three copies is how two surfaces come to name one cue two ways. */
export const CUE_LABEL: Record<CueKind, string> = {
  world: 'THE WORLD DISAGREED',
  retracted: 'A CITED WORK HAS BEEN RETRACTED',
  stale: 'NUMBER OLDER THAN THE EVIDENCE',
  // The panel chip's own reading, because it fires on the chip's own predicate
  // (`panelSplit`): the card and the coach must not name one panel two ways.
  lenses: PANEL_CHIP_LABEL.disagreed.toUpperCase(),
  // Two CLAIMS, not two readings: "reading" is the word a source check's result
  // owns (`READING_LABEL`), and this cue fires on a claim and the objection raised
  // against it in reasoning, whether or not either was ever checked — a label
  // naming readings described checks that may never have happened.
  contra: 'THESE TWO CLAIMS CAN’T BOTH STAND',
  blast: 'MOST OF THE BOARD HANGS ON THIS',
  flat: 'MOVES WITHOUT MOVEMENT',
  thin: 'THINNEST LOAD-BEARING CLAIM',
  // What the claims ARE rather than what they lack: "nothing outside the model"
  // read as an absence to a newcomer, and the plain version of the same fact is
  // that the model's word is all there is so far — which the metric then counts.
  unchecked: 'ONLY THE MODEL’S WORD SO FAR',
  onemodel: 'ONE MODEL WROTE THIS WHOLE BRANCH',
  test: 'CLOSEST THING TO AN EXPERIMENT',
  decide: 'YOU MAY BE DONE HERE',
  join: 'TWO BRANCHES, ONE CONSTRAINT',
  frontier: 'NOTHING HAS GROWN FROM THIS',
  strong: 'STRONGEST CLAIM NEVER ATTACKED',
  prune: 'CLOSED, STILL ON THE BOARD',
  onestudy: 'THESE REPORTS MAY BE ONE STUDY',
  unexplained: 'NOTHING PROPOSED TO EXPLAIN THIS',
  unsearched: 'AN INSIGHT NOBODY HAS SEARCHED FOR',
}

/**
 * Reading order under the RISK lens. Nineteen distinct integers, highest first,
 * and it is an editorial ordering stated as one — never a measurement.
 *
 * The shape of the table is one argument: a RECORD outranks a RATING, always.
 * The top six are things that happened (an observation, a withdrawn source, a
 * record below an unrevised number, a stored disagreement, an open
 * contradiction, reports that may be one study counted as several); the middle
 * is what is missing; the bottom is housekeeping.
 *
 * `onestudy` closes the records, directly under `contra` and above everything
 * that is missing. It is about sources that ARE there, so it outranks every cue
 * about sources that are not; and it is below the five above it because what it
 * reads is a resemblance a person has to confirm, where theirs are records
 * already made.
 *
 * `retracted` enters directly under `world` and above everything else, because
 * the two are the only cues here about evidence that has been TAKEN AWAY. It is
 * below `world` because an outcome you recorded speaks about the CLAIM and a
 * retraction speaks about the EVIDENCE under it: a claim whose support was
 * withdrawn may still be true, and a claim your own experiment contradicted is
 * the louder finding. It is above `stale` because `stale` is bookkeeping over
 * evidence that still stands, while this one says the evidence does not — and
 * the check is leaning on it right now.
 */
export const CUE_RISK_RANK: Record<CueKind, number> = {
  world: 19, // an observation went against the claim — the strongest record here
  retracted: 18, // the publisher withdrew a work this claim's check leaned on
  stale: 17, // a record landed and the number above it was never re-derived
  lenses: 16, // three stored votes that do not agree, on one node
  contra: 15, // an objection and its claim, both still open
  onestudy: 14, // several reports this claim counts that may be one study
  blast: 13, // the most of the board resting on something unchecked
  flat: 12, // the run spent moves and brought nothing back
  thin: 11, // the lowest-rated claim that is nonetheless carrying others
  unchecked: 10, // the graph as a whole has never been outside the model
  onemodel: 9, // one house wrote a whole branch and nothing else read it
  // A proposal whose only claim to being unstated is its model's memory — the
  // missing record most likely to be misread, as a claim nobody made.
  unsearched: 8,
  test: 7, // something designed to be run, never run
  decide: 6, // an ending worth considering, never an instruction to stop
  join: 5, // a proposed pair nobody has searched for
  // A record of what does not fit, with nothing proposed to explain it: a chance,
  // not a risk — the record itself already reads under `world`, `lenses`, `contra`.
  unexplained: 4,
  frontier: 3, // an open end, which is a chance rather than a problem
  strong: 2, // a claim doing well on its own word — an invitation to attack it
  prune: 1, // a closed leaf still on the board, and last under every lens
}

/**
 * Reading order under the EXPERIMENT lens: what would put this graph in contact
 * with the world soonest. Same table discipline, different question — so
 * `blast` and `onemodel` fall (both are answered by more model passes) and
 * `test`, `unchecked` and `join` rise (each ends in a search or a measurement).
 *
 * `retracted` falls two places from its RISK rank, to just under `world` and just
 * over `unchecked`. Under this lens it is a record somebody else already made and
 * this app only has to read — so it cannot outrank the two cues that end in
 * something being run, or the observation you can read for free. It stays over
 * `unchecked` because the branch under it is worse off than unchecked: it has a
 * source check, and the check is standing on a withdrawn paper, so the re-check
 * has a named target rather than a whole pool to choose from.
 */
export const CUE_EXP_RANK: Record<CueKind, number> = {
  test: 19, // a designed test, waiting. Nothing else here touches the world
  decide: 18, // the question of whether the board is already enough to act on
  world: 17, // a real observation already landed; read it before spending again
  retracted: 16, // grounding that has been withdrawn; read it, then check again
  unchecked: 15, // the fastest route from nothing outside the model to something
  thin: 14, // grounding the weakest carrier beats re-arguing it
  unsearched: 13, // one search decides whether it has already been said, as `join`
  join: 12, // one search decides whether the pair is already joined
  strong: 11, // the claim most worth trying to break next
  frontier: 10, // an untouched end, cheap to take further
  // A reasoning pass is not the world; what it adds is a prediction with a kill
  // test, which is the nearest thing to contact a proposal can carry.
  unexplained: 9,
  lenses: 8, // a disagreement already recorded, only waiting to be read
  contra: 7, // the pair is on the board; resolving it is reading, not testing
  stale: 6, // the evidence already exists; this is bookkeeping over it
  onestudy: 5, // bookkeeping too, over sources already read — just under `stale`
  flat: 4, // a fact about the run rather than about the world
  onemodel: 3, // answered by another model, which is not the world
  blast: 2, // structure, and structure alone tests nothing
  prune: 1, // last, always
}

/**
 * What the highlight keeps lit while a cue is open, per kind.
 *
 * The renderer's two existing emphases, unchanged in meaning: `lineage` holds the
 * ancestor cone — the route that led to the claim — and `dependencies` holds that
 * route AND everything hanging below it. The question each row answers is what
 * the cue's sentence is actually about. If it is about what rests on the anchor,
 * the subtree has to be on screen or the sentence is unreadable; if it is about
 * the claim itself and how it came to be, lighting the subtree only crowds it.
 */
export const CUE_EMPHASIS: Record<CueKind, 'lineage' | 'dependencies'> = {
  world: 'dependencies', // the subject is the reasoning still standing under the record
  retracted: 'dependencies', // what rests on the claim rests on the withdrawn work too
  stale: 'dependencies', // the newer record is below; the stale number is above it
  blast: 'dependencies', // the cue is a count of what would move with it
  contra: 'dependencies', // the objection hangs off the anchor as a child
  lenses: 'dependencies', // the dissent is stored on the node and read with what it carries
  unchecked: 'dependencies', // a statement about the pool below the anchor, not the anchor
  onemodel: 'dependencies', // the branch is the subject; the anchor is only its top
  thin: 'dependencies', // "load-bearing" is a claim about what it carries
  flat: 'lineage', // the subject is a run that produced nothing to look down at
  test: 'lineage', // an experiment and what it was designed from
  decide: 'lineage', // anchored on the seed; the whole exploration is its lineage
  join: 'lineage', // the far end is named in alsoIds, not below the anchor
  frontier: 'lineage', // an open end has nothing below it by definition
  strong: 'lineage', // the question is what this claim rests on, not what rests on it
  prune: 'lineage', // a closed leaf has nothing below it either
  onestudy: 'lineage', // the subject is this claim's own check and what it cites
  unexplained: 'lineage', // the record itself and how the claim came to carry it
  unsearched: 'lineage', // a conjecture and what it was proposed from
}

/** How many pins the board shows at once. */
export const CUE_VISIBLE_MAX = 8
/** Skip a kind twice and it mutes. Skips count per kind, seen counts per
 *  instance — see `CueFilters`. */
export const SKIPS_TO_MUTE = 2
/** `unchecked` needs both: enough nodes to be a pattern, and enough of the pool
 *  to be the graph's condition rather than one corner of it. */
export const UNCHECKED_MIN = 3
export const UNCHECKED_SHARE = 0.6
/** Two stamped nodes agreeing is not a branch. Three is where "one model wrote
 *  all of this" starts being a fact about the reasoning. */
export const ONEMODEL_MIN_BRANCH = 3
/** The run's own stall definition, `RunStop`'s `stalled` in move rows. */
export const FLAT_STALL_MOVES = 3
/** Below this, "highest rated" and "only one rated" are the same sentence, and a
 *  cohort of one is not a comparison. Every rating cue is gated on it. */
export const COHORT_MIN = 2
/**
 * How many withdrawn works it takes. ONE, and it is not a tunable in disguise:
 * every other threshold in this file separates a pattern from an incident, and a
 * retraction is not a pattern — the second one says nothing the first did not.
 * Named and exported anyway, because the WHY line is built from it and invariant
 * 8 is about a sentence that cannot drift from its predicate.
 */
export const RETRACTED_MIN = 1
/**
 * How many claims standing on a refutation must still be open for `world` to
 * fire. ONE, for `RETRACTED_MIN`'s reason: a record that went against a claim is
 * not a pattern to wait for. Open, because the cue's sentence is about branches
 * that can still grow; the count it prints is every claim standing on the
 * record, open or closed (`standingOn`), which is the number the cards and the
 * report name.
 */
export const WORLD_MIN = 1
/**
 * How many rings below the seed a closed leaf must sit before `prune` offers it.
 *
 * The first ring is the seed's own answers: under a question a closed one is a
 * candidate ruled out, under a goal a route blocked, and its reason is an
 * argument about the seed itself rather than a detail of one branch. That is a
 * finding to keep on the board, and offering to delete it was offering to delete
 * the most load-bearing ending the graph has. Named for the rule's sake: the WHY
 * line prints it, so moving it moves the sentence.
 */
export const PRUNE_MIN_RING = 2

/**
 * The endings `prune` may offer: "path failed", and nothing else.
 *
 * Of the four endings (`core/endings.ts`) only a failed path is the argument simply
 * running out, which is the one thing "clutter" can mean on this board. An
 * objection that stood is a finding AGAINST its parent — the model raised it and
 * closed the path on it — and deleting it deletes the reason the parent is weaker
 * than its own rating says. A gap left open is a question waiting on the user or
 * the world. A claim a recorded result answered is a record somebody went and got.
 * An allow-list rather than a list of exceptions, so an ending added to the table
 * later is kept on the board until somebody decides it is clutter. Named for the
 * rule's sake: the WHY line prints these labels and the ones kept, so moving one
 * moves the sentence.
 */
export const PRUNE_ENDINGS: readonly Ending[] = ['path-failed']

/**
 * The widest page-text distance `onestudy` reads as two write-ups of one study:
 * 18 bits of the 64-bit signature, on two sites.
 *
 * Above `SYNDICATION_HAMMING_MAX` (10) the genealogy stops merging by itself,
 * because the two texts are no longer one artefact under two mastheads — but
 * they can still share most of their word triples, which is what a press
 * release rewritten by two newsrooms, or one abstract quoted at length on two
 * sites, looks like. Eighteen because it separates the two populations on the
 * signature `core/similarity.ts` actually computes: in a simulation over 60,000
 * pairs of unrelated synthetic 200-word texts none came within 18 bits (the first
 * appeared at 19, about 2 in 10,000), while texts sharing about 60% of their word
 * triples landed here about half the time. Real pages on one subject share more
 * phrasing than random text does, so the true false-alarm rate is higher than
 * that — which is exactly why this is a question and not a rule: nothing merges
 * on it, and the press that answers the cue is a person's decision, recorded as
 * theirs.
 */
export const ONESTUDY_HAMMING_MAX = 18

/**
 * The fewest words a title needs before two records carrying it word for word,
 * on two sites, may be one study.
 *
 * Six because the titles that repeat across unrelated pages are short — "Just a
 * moment...", "Access denied", "Page not found" — and a paper's or a story's own
 * title is rarely under six words. Titles are labels, which the genealogy never
 * hashes as evidence; here one is only a reason to ask.
 */
export const ONESTUDY_TITLE_MIN_WORDS = 6

/** The endings `prune` never offers — every ending not in `PRUNE_ENDINGS`, in the
 *  order endings are counted in, for the WHY line. */
const PRUNE_KEPT: readonly Ending[] = ENDING_ORDER.filter((e) => !PRUNE_ENDINGS.includes(e))

/** “a”, “b” or “c” — ending labels quoted as the card prints them, for a sentence. */
const endingsOr = (es: readonly Ending[]): string => {
  const q = es.map((e) => `“${ENDING_LABEL[e]}”`)
  return q.length < 2 ? (q[0] ?? '') : `${q.slice(0, -1).join(', ')} or ${q[q.length - 1]}`
}

/** `blast` wants a subtree, not a parent: one child is a link in a chain. */
const BLAST_MIN_SUBTREE = 2

const pct = (x: number): string => `${Math.round(x * 100)}%`

/**
 * The printed threshold forms each kind's rule must contain.
 *
 * Built from the constants above rather than typed out, so a threshold that
 * moves takes its rule string with it. The rule-contract test reads this map:
 * a cue whose WHY line does not state the numbers its predicate used fails the
 * build, which is what promotes the WHY line from copy to a contract.
 */
export const CUE_THRESHOLDS: Partial<Record<CueKind, readonly string[]>> = {
  unchecked: [String(UNCHECKED_MIN), pct(UNCHECKED_SHARE)],
  onemodel: [String(ONEMODEL_MIN_BRANCH)],
  flat: [String(FLAT_STALL_MOVES)],
  thin: [String(COHORT_MIN)],
  frontier: [String(COHORT_MIN)],
  strong: [String(COHORT_MIN)],
  blast: [String(BLAST_MIN_SUBTREE)],
  retracted: [String(RETRACTED_MIN)],
  world: [String(WORLD_MIN)],
  prune: [String(PRUNE_MIN_RING), ...PRUNE_ENDINGS.map((e) => ENDING_LABEL[e])],
  onestudy: [
    String(SYNDICATION_HAMMING_MAX),
    String(ONESTUDY_HAMMING_MAX),
    String(ONESTUDY_TITLE_MIN_WORDS),
  ],
}

/**
 * Channel per kind, fixed. A cue is coloured by what it is about — `world` and
 * `stale` and `unchecked` all speak for the record channel — and resolving it
 * from a table rather than per call site is what keeps the pin, the card and the
 * highlight the same colour.
 */
const CUE_CHANNEL: Record<CueKind, Channel> = {
  world: 'refuted',
  // The record channel, with `world`, `stale` and `unchecked`: every cue whose
  // subject is what the evidence does or does not say reads in one colour.
  retracted: 'refuted',
  stale: 'refuted',
  lenses: 'gap',
  contra: 'tension',
  blast: 'tension',
  flat: 'gap',
  thin: 'weak',
  unchecked: 'refuted',
  onemodel: 'weak',
  test: 'insight',
  decide: 'insight',
  join: 'gap',
  frontier: 'signal',
  strong: 'insight',
  prune: 'dead',
  // `onemodel`'s colour, for `onemodel`'s reason: support that looks wider than
  // it is — one house there, one study here.
  onestudy: 'weak',
  // A conjecture's own channel, which is what the press lands.
  unexplained: 'insight',
  // With `join`: a search nobody has run.
  unsearched: 'gap',
}

/** One action per kind, as a descriptor. The UI dispatches it on a press; this
 *  module never does. */
const CUE_ACTION: Record<CueKind, CueAction> = {
  world: { kind: 'inspect', label: 'Open the record' },
  // `inspect`, which prices null — a citation somebody has withdrawn must never
  // be the thing that gates a spend. The check is already on the node; reading
  // which passage came out of the retracted work costs nothing.
  retracted: { kind: 'inspect', label: 'See the source' },
  stale: { kind: 'inspect', label: 'Read what landed below' },
  lenses: { kind: 'inspect', label: 'Read the dissent' },
  contra: { kind: 'inspect', label: 'Resolve the pair' },
  blast: { kind: 'verify', label: 'Check against sources' },
  flat: { kind: 'report', label: 'Read the run account' },
  // Not a check on the anchor. The anchor is chosen by the model's own rating, and
  // what a press spends on must not be: the claims it checks are the ones most of the
  // board rests on, counted, and the anchor is among them only if the count says so.
  thin: { kind: 'checkResting', label: `Check the ${RESTING_CHECKS} claims most of this rests on` },
  unchecked: { kind: 'verify', label: 'Check against sources' },
  onemodel: { kind: 'audit', label: 'Cross-examine on another model' },
  test: { kind: 'record', label: 'Record what happened' },
  decide: { kind: 'report', label: 'Read the run account' },
  join: { kind: 'linkScan', label: 'Search for the join' },
  frontier: { kind: 'deepen', label: 'Deepen it' },
  strong: { kind: 'challenge', label: 'Challenge it' },
  prune: { kind: 'prune', label: 'Prune the branch' },
  // Free: a same-family override per join, written where the Lab's own form
  // writes one and shown in its merge ledger (`ui/groupPress.ts`).
  onestudy: { kind: 'group', label: 'Group them as one study' },
  // The card's own press, by its name (D155): the button runs `runConjecture` on
  // the record, which is what the card's "Propose insights" (D333), U and the palette
  // run on a selected node. It said "Propose what explains it", one word from the
  // sweep item's "Propose what explains this" — a different pass, told which item
  // to explain — while running the card's.
  unexplained: { kind: 'conjecture', label: 'Propose insights' },
  unsearched: { kind: 'priorArt', label: 'Search for prior art' },
}

/**
 * The WHY lines. Every one of them starts `FIRED ON · ` and states the actual
 * condition — the sentence a reader can check the graph against by hand.
 */
const CUE_RULE: Record<CueKind, string> = {
  world: `FIRED ON · an outcome recorded against this claim with the bearing “refuted” — or, with no such outcome, a source check still current with its wording that the citation gate admitted as refuted — and at least ${WORLD_MIN} still-open node standing on it: reasoned or last rewritten before that record landed, with no result of its own or between. Counted down every strand, a synthesis’s second parent included, and under the nearest refutation where two stand above a node — the claims whose cards name this one, as the report lists them, open or closed.`,
  retracted: `FIRED ON · a source check still current with this claim’s wording citing at least ${RETRACTED_MIN} passage from a work Crossref’s retraction data reports as retracted, withdrawn or removed. A correction or an expression of concern is not counted here, and the notice is read off the stored citation rather than out of its title.`,
  stale:
    'FIRED ON · a record below this node — an outcome of any bearing, or a live refuted or mixed source check — dated after this node’s own wording, with nothing re-derived here since.',
  lenses: `FIRED ON · a stored panel whose decided votes are not unanimous. Agreement is exact equality, so “${PANEL_VERDICT_LABEL.holds.toLowerCase()}” beside “${PANEL_VERDICT_LABEL['holds-weaker'].toLowerCase()}” is a split.`,
  contra:
    'FIRED ON · a contradiction hanging directly off this claim with neither of them closed and neither carrying a recorded result.',
  blast: `FIRED ON · at least ${BLAST_MIN_SUBTREE} nodes standing below this one, and nothing outside the model behind it — no recorded outcome, no live source check, no live cross-examination — and never on an insight, which a source check could only ever find nothing for.`,
  flat: `FIRED ON · the finished run stopping stalled, or its last ${FLAT_STALL_MOVES} moves all coming back with nothing new. Both are counts over stored move rows, never a reading of what the model said.`,
  thin: `FIRED ON · the lowest authored confidence among nodes that carry other nodes, compared only inside the largest single-model cohort, only where that cohort holds at least ${COHORT_MIN} eligible nodes, never over a claim you have rewritten, and never over an insight, whose confidence is set on a lower scale on purpose.`,
  unchecked: `FIRED ON · ${UNCHECKED_MIN} or more nodes below the seed with no recorded outcome, no live source check and no live cross-examination, and those nodes being at least ${pct(UNCHECKED_SHARE)} of the pool. Insights count toward that share and are never the anchor: a source check could only ever find nothing for one.`,
  onemodel: `FIRED ON · a first-ring branch of ${ONEMODEL_MIN_BRANCH} or more nodes, every one of them stamped by the same model and none of them rewritten by you, with no live cross-examination anywhere in it.`,
  test: 'FIRED ON · a node written as an experiment with no result recorded against it, waiting — since it was written, or since you last rewrote it — longer than any other open experiment here.',
  decide:
    'FIRED ON · at least one grounded record on the board, and either the run stopping with nothing left on its list or what remains under the ceiling you set falling short of one more move.',
  join: 'FIRED ON · the proposed pair standing highest on the breakthrough ladder that carries no link scan, with both ends still readable and at least one of them on this canvas. Its score is not read.',
  frontier: `FIRED ON · the highest authored confidence among leaves with nothing below them, compared only inside the largest single-model cohort, only where that cohort holds at least ${COHORT_MIN} eligible leaves, never over a claim you have rewritten, never over one reasoned before something above it was refuted and not re-derived since, and never over an insight, whose confidence is set on a lower scale on purpose.`,
  strong: `FIRED ON · the highest authored confidence among claims that have never been challenged, cross-examined or panelled and carry no contradiction of any origin, compared only inside the largest single-model cohort, only where that cohort holds at least ${COHORT_MIN} eligible claims, never over a claim you have rewritten, and never over an insight, whose confidence is set on a lower scale on purpose.`,
  onestudy: `FIRED ON · two or more reports this claim’s live source check cites (or the Lab binds to it) that the evidence genealogy counts as separate independence units, joined by a resemblance it computes but does not merge on: page text between ${SYNDICATION_HAMMING_MAX + 1} and ${ONESTUDY_HAMMING_MAX} bits apart on its 64-bit signature — sharing much of its phrasing, but past the ${SYNDICATION_HAMMING_MAX} bits at which the genealogy merges two pages as one artefact by itself — or the same title of at least ${ONESTUDY_TITLE_MIN_WORDS} words, in both cases on two different sites. A pair a person kept separate in the Lab is never offered, and grouping changes the independence count, never a reading.`,
  prune: `FIRED ON · a closed leaf at least ${PRUNE_MIN_RING} rings below the seed whose ending is ${endingsOr(PRUNE_ENDINGS)}, with nothing below it and nothing outside the model on it — no recorded outcome, no live source check, no live cross-examination — reasoned out, rather than refuted by a source, settled by a measurement or closed only by a constraint you set. A leaf whose ending is ${endingsOr(PRUNE_KEPT)} is never offered — an objection that stood is a finding against its parent, and a knowledge gap is a question reasoning can’t settle — and nor is a first-ring branch, which answers the seed itself.`,
  unexplained:
    'FIRED ON · a record of something that does not fit — a result or a source against a claim, a mixed reading, a split panel, an audit objection, an open contradiction or gap — on a claim with no insight hanging anywhere beneath it. Counted by parentage and never by the anchors an insight names; among several, the one with the most of the graph resting on it, and never an insight itself.',
  unsearched:
    'FIRED ON · an insight with no prior-art search of the wording it carries now — never searched, or searched only before a rewrite — the oldest on the board first. Its “no source the model knows” is still only its model’s report on its own memory.',
}

/** Said when `unchecked` does rate its anchor: the cohort rule, in the WHY line,
 *  because "highest rated" is only a fact next to the scale it was rated on. */
const UNCHECKED_RATED = `The anchor is the highest-rated of those nodes, compared only inside the largest single-model cohort, only where at least ${COHORT_MIN} of them carry that stamp, never over a claim you have rewritten, and never over an insight.`

/**
 * The two ways to have no comparison, and the rule prints the one that held.
 *
 * "No stamps anywhere" and "stamps, but not on enough of what is missing" are
 * different facts about the pool, and only one of them is true at a time. A WHY
 * line is a sentence the reader is invited to check the graph against, so
 * printing the wrong reason is the same failure as printing the wrong threshold.
 */
const UNCHECKED_NO_STAMP =
  'No node in the pool carries a model stamp, so no self-ratings are compared and the anchor is structural: the largest subtree with nothing outside the model behind it.'
const UNCHECKED_TOO_FEW_RATED = `Fewer than ${COHORT_MIN} of those nodes carry the stamp of the pool’s largest single-model cohort with the model’s own rating still on them, so no self-ratings are compared and the anchor is structural: the largest subtree with nothing outside the model behind it.`

/* ── the shared machinery ─────────────────────────────────────────────────
   Rebuilt here rather than imported from the store: the store imports this
   module, so `childrenIndex` and the descendant walk are local to keep the
   dependency one-way. Every walk is cycle-guarded, because a
   hand-edited document is allowed to be malformed and a coach that hangs the
   tab is worse than no coach. */

/** The confidence the producing model wrote, even if a later result moved the
 *  live dial. */
const authoredConfidence = (n: ReasonNode): number => n.outcome?.confidenceBefore ?? n.confidence

// When the wording now on screen was written is `writtenAt` (`./rewrite`): ONE
// reading of that question for this file and for the walks it has to agree
// with — a rewrite re-authors the claim, so `editedAt` wherever it exists.

/**
 * Whether the number on this node is still the MODEL'S number.
 *
 * `reviseNode` (`engine/reason.ts`) replaces the claim, stamps `editedAt` and
 * never asks anyone for a fresh rating — so the confidence left sitting there
 * rates wording the model never wrote. `persist.ts` draws exactly this line for
 * calibration, excluding nodes the user rewrote because "the claim and the
 * number on it are theirs now", and the four rating cues have to draw it too:
 * "the model rated this one lowest" over a claim the user typed is a sentence
 * about a comparison that never happened.
 *
 * Only the COMPARISON drops the node. A rewritten claim still counts as
 * ungrounded, still carries its subtree, and can still be `unchecked`'s
 * structural anchor — what it cannot be is the extreme of a scale it was never
 * measured on.
 */
const modelRated = (n: ReasonNode): boolean =>
  n.editedAt === undefined &&
  // A conjecture's "departs from", carried onto the graph, has placeholders
  // where ratings go: no model was asked. It is never the extreme of a scale.
  n.carriedFrom === undefined

/**
 * Whether a node's confidence sits on the scale the rating cues compare.
 *
 * A conjecture's does not. Its brief tells the model that 0.05–0.3 is honest for
 * most conjectures, so its number is set low ON PURPOSE, and comparing it with
 * ordinary claims is the two-scales error the cohort rule exists to refuse:
 * `thin` would crown every conjecture the thinnest claim on the board and press
 * a source check on it. That check is the one instrument that can only ever
 * answer "nothing found" for a proposal nobody has made — so no cue whose action
 * is a check anchors a conjecture either (`blast`, `unchecked`), whatever it
 * reads. A conjecture's next instrument is a search for prior art or a test.
 */
const ordinaryScale = (n: ReasonNode): boolean => n.kind !== 'conjecture'

/**
 * The report's definition of outside grounding, unchanged.
 *
 * A panel is deliberately absent: it is one model rereading itself, and counting
 * it here would let three passes of one house stand in for the world.
 */
const grounded = (n: ReasonNode): boolean =>
  // `outcomeLive`, not `!!n.outcome`, and the two siblings beside it are why:
  // both were already read through their own liveness predicate and the outcome
  // was not, so a result recorded against wording the user has since replaced
  // counted as outside grounding for a claim it never touched.
  outcomeLive(n) || (verificationLive(n) && checkedAgainstSources(n)) || (auditLive(n) && !!n.audit)

interface Pooled {
  node: ReasonNode
  /** Position in `session.order` — the durable arrival order, and the last
   *  tiebreak everywhere in this file. */
  index: number
  /** Primary-parent descendants, the graph's existing load-bearing arithmetic. */
  subtree: number
}

/** Primary parent only, in `order` first and then whatever `order` forgot, so
 *  child lists are deterministic on a malformed document too. */
function childrenIndex(s: Session): Map<string, string[]> {
  const children = new Map<string, string[]>()
  const placed = new Set<string>()
  const push = (n: ReasonNode) => {
    if (!n.parentId) return
    const list = children.get(n.parentId)
    if (list) list.push(n.id)
    else children.set(n.parentId, [n.id])
  }
  for (const id of s.order) {
    const n = s.nodes[id]
    if (!n || placed.has(id)) continue
    placed.add(id)
    push(n)
  }
  for (const n of Object.values(s.nodes)) {
    if (placed.has(n.id)) continue
    placed.add(n.id)
    push(n)
  }
  return children
}

function descendantsOf(s: Session, root: string, children: Map<string, string[]>): string[] {
  const out: string[] = []
  const seen = new Set<string>([root])
  const stack = [...(children.get(root) ?? [])]
  while (stack.length) {
    const id = stack.pop()!
    if (seen.has(id)) continue
    seen.add(id)
    if (!s.nodes[id]) continue
    out.push(id)
    for (const child of children.get(id) ?? []) stack.push(child)
  }
  return out
}

/** The one model whose scale may be read here, and the nodes on it. The
 *  grouping key stays local to `cohortOf`: `stamp` is what the metric line
 *  prints and `ids` is what the filters ask, and a field nobody reads is a field
 *  a later reader has to check the callers for. */
interface Cohort {
  stamp: ModelStamp
  ids: Set<string>
}

/**
 * The largest single-model group in the pool, ties to the stamp seen earliest.
 *
 * Invariant 3 in the form this layer needs it: two models' self-ratings are not
 * one scale, so a comparison that crossed stamps would be arithmetic over two
 * different meanings of 0.8.
 */
function cohortOf(pool: readonly Pooled[]): Cohort | null {
  const groups = new Map<string, { stamp: ModelStamp; ids: Set<string>; first: number }>()
  for (const p of pool) {
    const stamp = p.node.model
    if (!stamp) continue
    const key = spendKey(stamp)
    const group = groups.get(key)
    if (group) group.ids.add(p.node.id)
    else groups.set(key, { stamp, ids: new Set([p.node.id]), first: p.index })
  }
  let best: Cohort | null = null
  let bestSize = 0
  let bestFirst = Number.POSITIVE_INFINITY
  for (const group of groups.values()) {
    if (group.ids.size > bestSize || (group.ids.size === bestSize && group.first < bestFirst)) {
      best = { stamp: group.stamp, ids: group.ids }
      bestSize = group.ids.size
      bestFirst = group.first
    }
  }
  return best
}

/** The scale caveat, in the metric line, naming whose scale it is. */
const onScale = (stamp: ModelStamp): string => `ON ${stamp.name.toUpperCase()}'S OWN SCALE`

const plural = (n: number, one: string, many = `${one}S`): string => `${n} ${n === 1 ? one : many}`

/**
 * The last run that actually ended.
 *
 * Both fields, because they are written together at the close: a run with a
 * `finishedAt` and no `stop` is a record of a tab that died mid-run, and reading
 * a stop out of it would be inventing an ending nobody reached.
 */
export function latestFinishedRun(s: Session): Run | null {
  for (let i = s.runs.length - 1; i >= 0; i--) {
    const run = s.runs[i]
    if (run && run.finishedAt !== undefined && run.stop !== undefined) return run
  }
  return null
}

/**
 * What `onestudy` reads on one claim: its reports, the independence units the
 * Lab's genealogy counts them as, and the largest group of them the resemblance
 * signals join across units — or null when no such group exists.
 *
 * Exported because three doors ask it and must ask it the same way: the cue
 * below, the card's line under its status clause, and the press that answers
 * both (`ui/groupPress.ts`), which reads it again at the press rather than
 * trusting a cue made before the last check landed — a cue is JSON, and record
 * ids frozen into one could name a group a new check has since changed.
 */
export interface OneStudyReading {
  tally: IndependenceTally
  cluster: KinCluster
}

export function oneStudyOf(session: Session, nodeId: string): OneStudyReading | null {
  // Two reports at least, counted without the genealogy: the coach asks this of
  // every claim on the board, and most cannot have two (`reportBound`).
  if (reportBound(session, nodeId) < 2) return null
  const evidence = sessionEvidence(session)
  const ids = claimRecordIds(evidence, nodeId)
  if (ids.length < 2) return null
  const cluster = kinClusters(evidence.genealogy, ids, {
    hammingMax: ONESTUDY_HAMMING_MAX,
    titleMinWords: ONESTUDY_TITLE_MIN_WORDS,
  })[0]
  return cluster ? { tally: independenceTally(evidence.genealogy, ids), cluster } : null
}

/**
 * The question, as the cue and the card both ask it: "These 5 reports may
 * describe 1 study; group them?" — then which resemblance joined them, then what
 * grouping does and does not touch. One builder, so the two cannot word one
 * suggestion two ways.
 */
export function oneStudySentence(reading: OneStudyReading): string {
  const rules = new Set(reading.cluster.joins.map((j) => j.signal.rule))
  const how =
    rules.size > 1
      ? 'Between them they share a title or much of their page text, on different sites.'
      : rules.has('same-title')
        ? 'They carry the same title, on different sites.'
        : 'Their page text shares much of its phrasing, on different sites, without being one copy.'
  return `These ${reading.cluster.recordIds.length} reports may describe 1 study; group them? ${how}`
}

/**
 * Fire every kind that has a candidate, at most one cue per kind, unsorted.
 *
 * Order and retirement are `assembleCues`'s job, deliberately: this half is a
 * reading of the document and nothing else, so a test can name what fired
 * without a lens or a filter in the way.
 */
export function cueSetFor(session: Session, ctx: CueContext): Cue[] {
  const out: Cue[] = []
  const children = childrenIndex(session)

  const descCache = new Map<string, string[]>()
  const descendants = (id: string): string[] => {
    const hit = descCache.get(id)
    if (hit) return hit
    const walked = descendantsOf(session, id, children)
    descCache.set(id, walked)
    return walked
  }

  // Focus scopes the pool to the focused node and everything under it, the rule
  // the retired First Read planner used.
  let scope: Set<string> | null = null
  if (ctx.focusId && session.nodes[ctx.focusId]) {
    scope = new Set<string>([ctx.focusId, ...descendants(ctx.focusId)])
  }

  // The coach never comments on the move just made. The newest node is the last
  // one in arrival order, and only a hand-made one is excluded: a node a run
  // produced is exactly what this layer exists to read.
  let justMadeId: string | null = null
  for (let i = session.order.length - 1; i >= 0; i--) {
    const id = session.order[i]
    const n = session.nodes[id]
    if (!n) continue
    if (n.origin === 'manual') justMadeId = n.id
    break
  }

  const pool: Pooled[] = []
  session.order.forEach((id, index) => {
    const node = session.nodes[id]
    if (!node || id === justMadeId) return
    if (scope && !scope.has(id)) return
    pool.push({ node, index, subtree: descendants(id).length })
  })
  if (!pool.length) return out

  const byId = new Map<string, Pooled>(pool.map((p) => [p.node.id, p]))
  const cohort = cohortOf(pool)

  /** Subtree size desc, then earliest arrival. The tiebreak everywhere below. */
  const heavier = (a: Pooled, b: Pooled): number => b.subtree - a.subtree || a.index - b.index

  /**
   * A terminal node is a path the coach must not send anyone back down, and the
   * seed is the user's own words rather than a claim the model made. Three cues
   * have one of those as their whole subject and say so at their own call
   * sites: `prune` and `world` anchor closed nodes, `decide` anchors the seed.
   */
  const anchorable = (p: Pooled): boolean => !p.node.terminal && p.node.parentId !== null

  const add = (
    kind: CueKind,
    anchor: ReasonNode,
    parts: {
      body: string
      metric: string
      rule?: string
      alsoIds?: string[]
      /** The record this instance is about — see `Cue.key`. Omitted where the
       *  anchor is the whole identity. */
      keyedTo?: string | number
    },
  ): void => {
    out.push({
      key:
        parts.keyedTo === undefined
          ? `${kind}:${anchor.id}`
          : `${kind}:${anchor.id}:${parts.keyedTo}`,
      kind,
      anchorId: anchor.id,
      alsoIds: parts.alsoIds ?? [],
      channel: CUE_CHANNEL[kind],
      title: anchor.label,
      body: parts.body,
      metric: parts.metric,
      rule: parts.rule ?? CUE_RULE[kind],
      riskRank: CUE_RISK_RANK[kind],
      expRank: CUE_EXP_RANK[kind],
      action: { ...CUE_ACTION[kind] },
    })
  }

  /* 1 · world — a record went against a claim, and reasoning below it predates
     the record. The strongest cue because the record is the strongest in the
     app: an observation outranks every rating, and so does a refutation the
     citation gate admitted (invariant 2's bar, which is the loudest channel's).

     Both records `taint.ts` walks down from, in its order: a refuting RESULT
     first, then a live refuting CHECK. The check was missing here, so a graph
     whose one refutation came from its sources — the only kind a reader who
     records nothing ever gets — had a coach that said nothing about the branch
     reasoned before it, and offered to deepen a leaf of it (`frontier`). */
  let worldAnchorId: string | null = null
  {
    let best: Pooled | null = null
    let bestCount = 0
    let bestBy: 'outcome' | 'verification' = 'outcome'
    let bestAt = 0
    for (const p of pool) {
      const outcome = p.node.outcome
      // Closed is allowed here, and only here besides `prune`. The subject is
      // the record and the reasoning still standing under it, not the path, so
      // the model marking the path exhausted must not silence it. `channelOf`
      // settles the same conflict the same way: an outcome-refuted node reads
      // refuted rather than dead, because terminal is the model's own judgement
      // and the loudest record in the app is not erasable by it.
      if (p.node.parentId === null) continue
      const v = p.node.verification
      const by =
        outcome?.bearing === 'refuted' && outcomeLive(p.node)
          ? ('outcome' as const)
          : v?.reading === 'refuted' && verificationLive(p.node)
            ? ('verification' as const)
            : null
      if (!by) continue
      const at = by === 'outcome' ? outcome!.recordedAt : v!.checkedAt
      // What stands on this record is what every card standing on it says it
      // does — the taint walk's own set, read back by source (`standingOn`).
      // Walking the tree by parent here counted a synthesis joined from this
      // branch nowhere, walked on through a node with a result of its own, and
      // counted under this claim what stands on a nearer refutation below it:
      // three ways for the coach and the card to count one record differently.
      // The walk dates each node by `writtenAt`, as `stale` does, so a claim
      // rewritten after the record is not in it, whatever its arrival says.
      const standing = standingOn(session, p.node.id)
      const open = standing.filter((id) => !session.nodes[id]?.terminal).length
      if (open < WORLD_MIN) continue
      const count = standing.length
      if (!best || count > bestCount || (count === bestCount && heavier(p, best) < 0)) {
        best = p
        bestCount = count
        bestBy = by
        bestAt = at
      }
    }
    if (best) {
      worldAnchorId = best.node.id
      add('world', best.node, {
        // Claims exactly the counted set and no more: a sibling rewritten after
        // the record is honestly re-derived, and a body saying "nothing under it
        // has been re-derived" would contradict the graph in that mixed case.
        // "Recorded", never "you recorded": the worked example carries an
        // authored result, and this sentence was telling a newcomer who had
        // recorded nothing that they had. Who recorded it is the card's to say
        // (the demo's own note under the result), not a guess made here.
        body:
          bestBy === 'outcome'
            ? 'A result recorded on this claim went against it, and reasoning below it still stands from before that record landed. Read the record before those branches grow again.'
            : 'A source check read this claim as stating the opposite — opened pages on independent sites — and reasoning below it still stands from before that check landed. Read the record before those branches grow again.',
        metric: `${plural(bestCount, 'NODE')} BELOW ${bestCount === 1 ? 'WAS' : 'WERE'} REASONED BEFORE THE RECORD`,
        // The record, so a SECOND result or check against this claim is a second
        // cue rather than one silently already answered.
        keyedTo: bestAt,
      })
    }
  }

  /* 2 · retracted — the check on this claim cited a work its publisher has
     withdrawn. The only cue in this file whose subject is a third party's record
     about the EVIDENCE rather than about the claim, and the one place the coach
     reads a citation rather than a rating.

     It reads `notice`, the field on the stored cite, and never the doc title:
     the title carries ' — RETRACTED' too, because that is what travels into a
     prompt and an export, but a retraction notice's OWN title says the same
     words — so a cue that matched on prose would fire hardest on the paper
     correctly citing the withdrawal. `flagged` is deliberately silent here: a
     correction is context, and a coach that shouted at every erratum would teach
     its reader to stop looking. */
  {
    let best: Pooled | null = null
    let bestCount = 0
    let bestCites = 0
    let bestAt = 0
    for (const p of pool) {
      // `anchorable`, like almost everything here: the camera walks to the
      // anchor, and a closed path is one the coach must not send anybody back
      // down. `world` and `prune` are the exceptions because a closed node is
      // their whole subject; this cue's subject is a check still standing under
      // live reasoning.
      if (!anchorable(p) || !verificationLive(p.node)) continue
      const cites = p.node.verification?.corpus?.cites ?? []
      // WORKS, counted by `docId`, because that is the word the metric prints and
      // because one paper quoted three times is one thing a publisher took back.
      // Counting cites instead read "2 RETRACTED WORKS" off two passages of one
      // paper, and — worse, since it moves the camera — ranked that node above one
      // leaning on two genuinely separate withdrawn works. The threshold is
      // untouched by this: at RETRACTED_MIN = 1 the two readings are the same
      // predicate, because one retracted passage IS one distinct withdrawn work.
      // `retractedWorks` is that count, and the one the card's status line reads.
      const works = retractedWorks(p.node).length
      if (works < RETRACTED_MIN) continue
      if (!best || works > bestCount || (works === bestCount && heavier(p, best) < 0)) {
        best = p
        bestCount = works
        bestCites = cites.length
        bestAt = p.node.verification!.checkedAt
      }
    }
    if (best) {
      add('retracted', best.node, {
        body: 'The source check on this claim cites a work that has since been retracted. That is the publisher withdrawing the record, not a verdict on your claim — but part of what this claim was checked against has been taken back.',
        // Two numbers counting two different things, deliberately: distinct
        // withdrawn works on the left, and every passage the check cited —
        // withdrawn or standing — on the right, which is what ON THIS CHECK says.
        metric: `${plural(bestCount, 'RETRACTED WORK')} CITED · ${plural(bestCites, 'PASSAGE')} ON THIS CHECK`,
        // The check, so re-running one that lands on the retracted work again is
        // a new pass to answer rather than the one already dismissed.
        keyedTo: bestAt,
      })
    }
  }

  /* 3 · stale — the reverse direction: the record is BELOW and the number above
     it was never revisited. Skips the `world` anchor, which already says a
     louder version of this about the same node. */
  {
    let best: Pooled | null = null
    let newest: { id: string; at: number } | null = null
    let bestCount = 0
    for (const p of pool) {
      const n = p.node
      if (!anchorable(p) || n.outcome || n.id === worldAnchorId) continue
      const written = writtenAt(n)
      let count = 0
      let latest: { id: string; at: number } | null = null
      for (const id of descendants(n.id)) {
        const d = session.nodes[id]
        if (!d) continue
        const at = recordAfter(d, written)
        if (at === null) continue
        count++
        if (!latest || at > latest.at) latest = { id, at }
      }
      if (!count || !latest) continue
      if (!best || heavier(p, best) < 0) {
        best = p
        bestCount = count
        newest = latest
      }
    }
    if (best && newest) {
      add('stale', best.node, {
        body: 'Something was recorded below this node after its wording was written, and this node has not been re-derived since. The number on it is older than the evidence under it.',
        metric: `${plural(bestCount, 'RECORD')} BELOW POSTDATE${bestCount === 1 ? 'S' : ''} THIS REASONING`,
        alsoIds: [newest.id],
        // The newest record's own timestamp: another one landing below this
        // number is a new fact about it, not the one already answered.
        keyedTo: newest.at,
      })
    }
  }

  /* 4 · lenses — one model, three disciplines, a stored disagreement. */
  {
    let best: Pooled | null = null
    for (const p of pool) {
      if (!anchorable(p) || !p.node.panel || !panelSplit(p.node.panel)) continue
      if (!best || heavier(p, best) < 0) best = p
    }
    if (best?.node.panel) {
      const tally = tallyPanel(best.node.panel)
      add('lenses', best.node, {
        // Not "three lenses read this claim". Three are ASKED; `tally.returned`
        // is how many came back and `tally.decided` how many took a position, and
        // a pass can fail or abstain. The body claims only what `panelSplit`
        // actually found — the passes that returned did not agree — and the
        // metric prints the counts.
        body: 'The lenses that returned did not land in the same place. The dissent is stored in the passes’ own words.',
        metric: `${tally.agreed} OF ${tally.decided} DECIDED VOTES AGREE · ${tally.asked} LENSES ASKED`,
        // The panel, so a re-run panel on the same claim is a new disagreement
        // rather than the one already read.
        keyedTo: best.node.panel.checkedAt,
      })
    }
  }

  /* 5 · contra — a claim and an objection to it, both still open. Where the
     objection came from a cross-examination, another house wrote it, and the
     metric says so: that is the honest version of "the models disagreed". */
  {
    let best: Pooled | null = null
    let objection: ReasonNode | null = null
    for (const p of pool) {
      if (!anchorable(p) || p.node.outcome) continue
      let found: ReasonNode | null = null
      for (const id of children.get(p.node.id) ?? []) {
        const c = session.nodes[id]
        if (!c || c.kind !== 'contradiction' || c.terminal || c.outcome) continue
        found = c
        break
      }
      if (!found) continue
      if (!best || heavier(p, best) < 0) {
        best = p
        objection = found
      }
    }
    if (best && objection) {
      const foreign =
        objection.origin === 'audit'
          ? ` · RAISED BY ${(objection.model?.name ?? 'ANOTHER PROVIDER').toUpperCase()}, ANOTHER PROVIDER`
          : ''
      add('contra', best.node, {
        body: 'An objection hangs directly off this claim and neither of them has been closed. One of the two readings has to go.',
        metric: `1 OPEN CONTRADICTION · NO RESULT RECORDED ON EITHER${foreign}`,
        alsoIds: [objection.id],
      })
    }
  }

  /* 6 · blast — the largest subtree standing on something nothing outside the
     model has touched. Structure, and the body says so rather than implying the
     claim is wrong. */
  {
    let best: Pooled | null = null
    for (const p of pool) {
      if (!anchorable(p) || p.subtree < BLAST_MIN_SUBTREE || grounded(p.node)) continue
      if (!ordinaryScale(p.node)) continue
      if (!best || heavier(p, best) < 0) best = p
    }
    if (best) {
      add('blast', best.node, {
        body: 'More of the board rests on this claim than on any other with nothing outside the model behind it. If it moves, everything under it moves with it.',
        metric: `${plural(best.subtree, 'NODE')} DOWNSTREAM · NO OUTCOME, LIVE CHECK, OR LIVE AUDIT`,
      })
    }
  }

  /* 7 · flat — a fact about the run's own move rows, never about the idea. The
     anchor is the last node the run actually worked, and it may be closed: it
     names a move that happened rather than a claim to take further. It may not
     be the seed, which is the one exclusion the walk below applies. */
  {
    const run = ctx.run
    const moves = run?.moves ?? []
    const tail = moves.slice(-FLAT_STALL_MOVES)
    const wentQuiet = moves.length >= FLAT_STALL_MOVES && tail.every((m) => m.outcome === 'nothing')
    if (run && run.finishedAt !== undefined && (run.stop === 'stalled' || wentQuiet)) {
      let anchor: Pooled | null = null
      for (let i = moves.length - 1; i >= 0; i--) {
        const hit = byId.get(moves[i]!.nodeId)
        // A run works the seed like any other node — the opening moves of a
        // fresh run usually are the seed — and the seed anchors nothing but a
        // decision. So walk back to the last move on a claim the model wrote,
        // and if there was none, fire nothing rather than point at the prompt.
        if (hit && hit.node.parentId !== null) {
          anchor = hit
          break
        }
      }
      let quiet = 0
      for (let i = moves.length - 1; i >= 0 && moves[i]!.outcome === 'nothing'; i--) quiet++
      const facts: string[] = []
      if (quiet > 0) facts.push(`${plural(quiet, 'MOVE')} IN A ROW RETURNED NOTHING NEW`)
      if (run.stop === 'stalled') facts.push('THE RUN STOPPED STALLED')
      if (anchor) {
        add('flat', anchor.node, {
          body: 'The run kept taking moves and they kept coming back empty. This is the node it was working when it went quiet.',
          metric: facts.join(' · '),
          // The run, so a later run that also went quiet on this node is a
          // second report and not the one already read.
          keyedTo: run.id,
        })
      }
    }
  }

  /* 8 · thin — a rating cue, so it lives entirely inside one cohort and names
     the scale. Grounding beats re-arguing, hence a press that checks what the
     board rests on — ranked by what sits beneath each claim, never by a rating. */
  if (cohort) {
    const eligible = pool.filter(
      (p) =>
        cohort.ids.has(p.node.id) &&
        modelRated(p.node) &&
        ordinaryScale(p.node) &&
        anchorable(p) &&
        (children.get(p.node.id)?.length ?? 0) > 0 &&
        channelOf(p.node) !== 'dead' &&
        channelOf(p.node) !== 'refuted',
    )
    if (eligible.length >= COHORT_MIN) {
      let best = eligible[0]!
      for (const p of eligible) {
        const d = authoredConfidence(p.node) - authoredConfidence(best.node)
        if (d < 0 || (d === 0 && heavier(p, best) < 0)) best = p
      }
      add('thin', best.node, {
        body: 'Of the claims that carry other claims, the model rated this one lowest. That is its own scale, compared only against claims the same model wrote.',
        metric: `CONF ${authoredConfidence(best.node).toFixed(2)} ${onScale(cohort.stamp)} · ${best.subtree} DERIVED`,
      })
    }
  }

  /* 9 · unchecked — a fact about the whole pool, anchored somewhere you can
     press. The anchor is rated where a cohort exists and structural where none
     does, and the rule says which of the two it was. */
  {
    const nonSeed = pool.filter((p) => p.node.parentId !== null)
    const open = nonSeed.filter((p) => !grounded(p.node))
    const fires = open.length >= UNCHECKED_MIN && open.length >= UNCHECKED_SHARE * nonSeed.length
    // Conjectures count in `open` — nothing outside the model has touched them,
    // which is true — but never anchor it: the press is a source check.
    const candidates = open.filter((p) => anchorable(p) && ordinaryScale(p.node))
    if (fires && candidates.length) {
      // The rating branch, so a rewritten claim is out of it — but it stays in
      // `open` and in `candidates`, because "nothing outside the model has
      // touched this" is true of it however the words got there, and the
      // structural anchor is a subtree count rather than a comparison.
      const rated = cohort
        ? candidates.filter((p) => cohort.ids.has(p.node.id) && modelRated(p.node))
        : []
      let best: Pooled
      let body: string
      let rule: string
      let metric: string
      // A rating cue, so the cohort minimum applies to it exactly as it does to
      // `thin`, `frontier` and `strong`: with one rated candidate, "the model
      // rated this one highest" is a comparison in which nothing was compared.
      if (cohort && rated.length >= COHORT_MIN) {
        best = rated[0]!
        for (const p of rated) {
          const d = authoredConfidence(p.node) - authoredConfidence(best.node)
          if (d > 0 || (d === 0 && heavier(p, best) < 0)) best = p
        }
        body =
          'Most of this graph has never been outside the model — no recorded result, no live source check, no cross-examination. The model rated this one highest of what is missing, on its own scale.'
        metric = `${open.length} OF ${nonSeed.length} NODES CARRY NOTHING OUTSIDE THE MODEL · CONF ${authoredConfidence(best.node).toFixed(2)} ${onScale(cohort.stamp)}`
        rule = `${CUE_RULE.unchecked} ${UNCHECKED_RATED}`
      } else {
        best = candidates[0]!
        for (const p of candidates) if (heavier(p, best) < 0) best = p
        body =
          'Most of this graph has never been outside the model — no recorded result, no live source check, no cross-examination. Nothing here is rated on a scale this app will compare, so the anchor is the claim the most rests on.'
        metric = `${open.length} OF ${nonSeed.length} NODES CARRY NOTHING OUTSIDE THE MODEL · ${plural(best.subtree, 'NODE')} DOWNSTREAM`
        rule = `${CUE_RULE.unchecked} ${cohort ? UNCHECKED_TOO_FEW_RATED : UNCHECKED_NO_STAMP}`
      }
      add('unchecked', best.node, { body, metric, rule })
    }
  }

  /* 10 · onemodel — a first-ring branch written end to end by one house, with
     nothing from outside it. The action is the pass built for exactly this. */
  {
    let best: Pooled | null = null
    let bestSize = 0
    let bestStamp: ModelStamp | null = null
    for (const p of pool) {
      if (!anchorable(p) || p.node.parentId === null) continue
      const parent = session.nodes[p.node.parentId]
      if (!parent || parent.parentId !== null) continue
      const family = [p.node.id, ...descendants(p.node.id)].filter((id) => byId.has(id))
      if (family.length < ONEMODEL_MIN_BRANCH) continue
      let key: string | null = null
      let stamp: ModelStamp | null = null
      let uniform = true
      for (const id of family) {
        const n = session.nodes[id]!
        // A rewritten node breaks the family exactly as a missing stamp does.
        // `editedAt` means the claim on screen is the user's words
        // (`persist.ts`'s calibration exclusion says so in as many words), so
        // one model did NOT write all of this — and the sentence this cue
        // prints would be false of the branch in front of the reader.
        if (!n.model || !modelRated(n) || (auditLive(n) && !!n.audit)) {
          uniform = false
          break
        }
        const k = spendKey(n.model)
        if (key === null) {
          key = k
          stamp = n.model
        } else if (k !== key) {
          uniform = false
          break
        }
      }
      if (!uniform || !stamp) continue
      if (
        !best ||
        family.length > bestSize ||
        (family.length === bestSize && heavier(p, best) < 0)
      ) {
        best = p
        bestSize = family.length
        bestStamp = stamp
      }
    }
    if (best && bestStamp) {
      add('onemodel', best.node, {
        body: 'One model wrote this whole branch and nothing from another house has read it. A model agreeing with itself is not agreement.',
        metric: `${plural(bestSize, 'NODE')} · ${bestStamp.name.toUpperCase()} ONLY`,
      })
    }
  }

  /* 11 · test — the one cue whose next step happens in the world. The metric
     counts every open experiment rather than claiming this is the only one. */
  {
    const open = pool.filter(
      (p) => anchorable(p) && p.node.kind === 'experiment' && !p.node.outcome,
    )
    if (open.length) {
      let best = open[0]!
      for (const p of open) {
        // Waiting is measured from `writtenAt`, not from arrival: a rewritten
        // experiment was re-authored then — `reviseNode` replaces the claim and
        // drops what hung below it — so the design nobody has run is that new,
        // whatever the original arrival time says.
        const d = writtenAt(p.node) - writtenAt(best.node)
        if (d < 0 || (d === 0 && heavier(p, best) < 0)) best = p
      }
      add('test', best.node, {
        body: 'This node describes something that could actually be run, and no result has been recorded against it. It has been waiting the longest.',
        metric: `${open.length} OPEN EXPERIMENT${open.length === 1 ? '' : 'S'} · THIS ONE HAS WAITED LONGEST`,
      })
    }
  }

  /* 12 · decide — a question, never a verdict, and anchored on the seed because
     the subject is the exploration rather than a claim. The budget half is read
     as an inequality: no figure is printed here or anywhere in this module. */
  {
    const seed = session.order.map((id) => session.nodes[id]).find((n) => n && !n.parentId)
    const groundedCount = pool.filter((p) => grounded(p.node)).length
    const emptyList = ctx.run?.stop === 'frontier'
    const capShort =
      ctx.capLeftUsd != null && ctx.nextMoveUsd != null && ctx.capLeftUsd < ctx.nextMoveUsd
    // Never under a focused reading. Focus scopes the pool to one subtree, and
    // "is what is on the board already enough to act on?" is a question about
    // the WHOLE exploration — asked, moreover, of a seed that focus has just
    // put outside the pool every other cue here is drawn from. A cue whose
    // anchor is not in the pool is a cue about a different graph.
    if (seed && !ctx.focusId && groundedCount > 0 && (emptyList || capShort)) {
      add('decide', seed, {
        body: emptyList
          ? 'Is what is on the board already enough to act on? The run has nothing left on its list, and there are records here to read the reasoning against.'
          : 'Is what is on the board already enough to act on? What is left under the ceiling you set will not cover another move, and there are records here to read the reasoning against.',
        metric: `${plural(groundedCount, 'GROUNDED RECORD')} ON THE BOARD · ${emptyList ? 'NOTHING LEFT ON THE RUN LIST' : 'THE CEILING YOU SET WILL NOT COVER ANOTHER MOVE'}`,
      })
    }
  }

  /* 13 · join — a proposed pair nobody has searched for. Both-foreign pairs are
     skipped: there would be nothing on this canvas to anchor or to scan from.
     The pair is the FIRST unsearched one in ladder order — `sortBridges`, which
     reads what each candidate has earned and never its score — so the cue and
     the tab agree about which pair is next. It used to be the highest-scoring
     one, which was the proposing model choosing what to search for. */
  {
    let best: { bridge: Bridge; anchor: Pooled } | null = null
    for (const bridge of sortBridges(session.bridges, session.nodes)) {
      if (bridge.linkage || !bridgeReadable(bridge, session.nodes)) continue
      // `bridgeReadable` only asks whether the end still resolves. The anchor
      // is where the camera lands, so it obeys the same gate as every other
      // anchor here: a closed end may be named in `alsoIds`, never walked to.
      const anchorId = localEnds(bridge).find((id) => {
        const end = byId.get(id)
        return !!end && anchorable(end)
      })
      if (!anchorId) continue
      const anchor = byId.get(anchorId)!
      best = { bridge, anchor }
      break
    }
    if (best) {
      const anchorId = best.anchor.node.id
      const also = localEnds(best.bridge).filter((id) => id !== anchorId && !!session.nodes[id])
      add('join', best.anchor.node, {
        body: 'Two branches were proposed as one join, and nothing has gone looking for it. A search that comes back empty is a place to look, not a finding.',
        metric: `${bridgeStanding(best.bridge, session.nodes).earned.length} OF ${BRIDGE_RUNGS_TOTAL} RUNGS EARNED · NEVER SEARCHED`,
        alsoIds: also,
        // The bridge, so answering the pair on this claim does not silence the
        // NEXT pair proposed off the same claim — the case that made the third
        // segment necessary at all.
        keyedTo: best.bridge.id,
      })
    }
  }

  /* 14 · frontier — a rating cue, same cohort discipline as `thin`. An open end
     is a chance rather than a problem, which is why it ranks low under RISK. */
  if (cohort) {
    const tainted = taintMap(session)
    const eligible = pool.filter((p) => {
      const n = p.node
      if (!cohort.ids.has(n.id) || !modelRated(n) || !anchorable(p) || n.outcome) return false
      if (!ordinaryScale(n)) return false
      if ((children.get(n.id)?.length ?? 0) > 0) return false
      // `channelOf` and not `kindChannelOf`, the same discipline `thin` keeps: a
      // leaf a source check refuted is still a mechanism by kind, and inviting
      // someone to build on it is the one thing this cue must never do.
      const channel = channelOf(n)
      if (channel !== 'signal' && channel !== 'insight') return false
      // …and one step further for the same reason: a leaf reasoned from a claim
      // since refuted wears its own hue, and "Deepen it" there grows exactly the
      // subtree `taint.ts` marks as owed a re-derivation. The card says what it
      // stands on; this cue must not invite building on it.
      return !tainted.has(n.id)
    })
    if (eligible.length >= COHORT_MIN) {
      let best = eligible[0]!
      for (const p of eligible) {
        const d = authoredConfidence(p.node) - authoredConfidence(best.node)
        if (d > 0 || (d === 0 && heavier(p, best) < 0)) best = p
      }
      add('frontier', best.node, {
        body: 'Nothing has grown from this yet, and of the open ends the model rated it highest. That is its own scale, compared only against claims the same model wrote.',
        metric: `CONF ${authoredConfidence(best.node).toFixed(2)} ${onScale(cohort.stamp)} · NOTHING BELOW IT`,
      })
    }
  }

  /* 15 · strong — `frontier`'s pair: one asks what a claim implies, this asks
     what breaks it. Nothing has attacked it yet, by three separate records. */
  if (cohort) {
    const eligible = pool.filter((p) => {
      const n = p.node
      if (!cohort.ids.has(n.id) || !modelRated(n) || !anchorable(p) || n.outcome) return false
      if (!ordinaryScale(n)) return false
      if (n.panel || (auditLive(n) && !!n.audit)) return false
      // Same reason as `frontier`: a refuted claim has already lost the contest
      // this cue is about, and "strongest claim never attacked" over one is a
      // sentence the record on the node contradicts.
      const channel = channelOf(n)
      if (channel !== 'signal' && channel !== 'insight') return false
      // ANY contradiction hanging off it, from any origin and in any state, is
      // an attack that happened. The origin test alone read only the objections
      // this app's own challenge pass wrote, so a cross-examination's objection
      // — or one the user typed — left the claim advertised as never attacked
      // with the attack sitting under it on the canvas. And closing an objection
      // does not un-raise it: "never attacked" is a fact about history.
      return !(children.get(n.id) ?? []).some((id) => {
        const c = session.nodes[id]
        return !!c && (c.origin === 'challenge' || c.kind === 'contradiction')
      })
    })
    if (eligible.length >= COHORT_MIN) {
      let best = eligible[0]!
      for (const p of eligible) {
        const d = authoredConfidence(p.node) - authoredConfidence(best.node)
        if (d > 0 || (d === 0 && heavier(p, best) < 0)) best = p
      }
      add('strong', best.node, {
        body: 'Nothing has attacked this — no challenge, no objection, no cross-examination, no panel — and of what the model wrote it rated this highest. A claim standing on its own word.',
        metric: `CONF ${authoredConfidence(best.node).toFixed(2)} ${onScale(cohort.stamp)} · NEVER CHALLENGED, OBJECTED TO, CROSS-EXAMINED OR PANELLED`,
      })
    }
  }

  /* 16 · prune — a closed node is its whole subject, and the body claims only
     what the graph shows: reasoned out, closed, nothing hanging from it, off the
     board on a press, back on an undo. It does NOT claim the leaf is costing
     anything to keep. Whether a terminal node travels in a prompt varies by op —
     `buildBridgeTurn` (`engine/prompts.ts`) drops terminal nodes without an
     outcome while the derive context's "elsewhere" bucket carries them — so any
     sentence about what this leaf spends would be true of some presses and false
     of others, and the honest version of that sentence is no sentence.

     Three things are never offered. A node with a RECORD on it — a recorded
     outcome, a live source check, a live cross-examination, which is `grounded`
     exactly — is the opposite of clutter: somebody went and got that, and a
     closed path with evidence under it is one of the more valuable things on the
     board. A node refuted by a source travels deliberately, and the model has to
     know what was refuted. And a constraint closure — `terminalKind:
     'constraint'` — means the argument did not die, the user's own bound closed
     it, and it comes back the moment they relax that bound; advising someone to
     delete a branch they can recover by changing their mind is advice this layer
     has no basis for.

     And since the endings got their own words, only one of them is offered
     (`PRUNE_ENDINGS`): "path failed", the argument running out. A closed
     knowledge gap is "open: reasoning can't settle it" — a question waiting on
     the user or the world, and the one the coach used to offer first. A terminal
     contradiction is "objection stood" — a finding against its parent, which the
     coach went on offering after the gap was excluded, as though a knocked-down
     claim's reason were clutter. A first-ring branch is an answer to the seed
     itself (`PRUNE_MIN_RING`), so its ending is a finding about the whole
     exploration rather than a leaf in the way. */
  {
    // Rings from the seed, walked up the parent chain rather than read off
    // `depth`, which a hand-edited document may leave stale. Cycle-guarded like
    // every walk here; a chain that breaks on a missing parent stops counting
    // there, which can only make a node look closer to the seed — the direction
    // in which nothing is offered.
    const ringOf = (n: ReasonNode): number => {
      let ring = 0
      const seen = new Set<string>()
      let cur: ReasonNode | undefined = n
      while (cur && cur.parentId !== null && !seen.has(cur.id)) {
        seen.add(cur.id)
        ring++
        cur = session.nodes[cur.parentId]
      }
      return ring
    }
    const offered = (n: ReasonNode): boolean => {
      // Null for a node still open, so "closed" is part of this test; and
      // `endingOf` rather than `reasonedEndingOf`, so a claim a result answered
      // reads as answered here, as it does on its card.
      const e = endingOf(n)
      return e !== null && PRUNE_ENDINGS.includes(e)
    }
    const closed = pool.filter(
      (p) =>
        offered(p.node) &&
        // `grounded` covers every recorded outcome. An inconclusive one answers
        // nothing, so its leaf still reads "path failed" — but somebody went and
        // got it, and that is a record rather than clutter.
        !grounded(p.node) &&
        p.node.terminalKind !== 'constraint' &&
        p.node.parentId !== null &&
        ringOf(p.node) >= PRUNE_MIN_RING &&
        (children.get(p.node.id)?.length ?? 0) === 0 &&
        channelOf(p.node) !== 'refuted',
    )
    if (closed.length) {
      let best = closed[0]!
      for (const p of closed) {
        const d = p.node.createdAt - best.node.createdAt
        if (d < 0 || (d === 0 && p.index < best.index)) best = p
      }
      add('prune', best.node, {
        body: 'This path was reasoned out and closed, and nothing hangs from it. Pruning takes it off the board, and undo puts it back.',
        metric: `CLOSED LEAF · ${closed.length} ON THE BOARD · NO RESULT RECORDED`,
      })
    }
  }

  /* 17 · onestudy — several reports one claim counts apart that may be one
     study. The Lab's genealogy asked about one claim, and the resemblance is read
     from the genealogy's own inputs below the bar at which it merges: the question
     goes to a person because the answer is not arithmetic. The largest group
     wins; weight breaks a tie. */
  {
    let best: { p: Pooled; reading: OneStudyReading } | null = null
    for (const p of pool) {
      if (!anchorable(p)) continue
      const reading = oneStudyOf(session, p.node.id)
      if (!reading) continue
      const d = reading.cluster.recordIds.length - (best?.reading.cluster.recordIds.length ?? 0)
      if (!best || d > 0 || (d === 0 && heavier(p, best.p) < 0)) best = { p, reading }
    }
    if (best) {
      const { tally, cluster } = best.reading
      add('onestudy', best.p.node, {
        body: `${oneStudySentence(best.reading)} Grouping counts them as one independence unit, here and in the Lab; it changes no reading.`,
        metric: `${plural(tally.reports, 'REPORT')} · ${tally.independent} INDEPENDENT · ${cluster.recordIds.length} MAY BE ONE STUDY`,
        // The group, so a second group on the same claim — or this one grown by
        // a later check — is a new question rather than the one already answered.
        keyedTo: fnv1a64(cluster.recordIds.join('\u0000')),
      })
    }
  }

  /* 18 · unexplained — a record of what does not fit, and nothing proposed to
     explain it. By PARENTAGE, never by the anchors a conjecture names: those are
     its model's own pointers, and a cue that read them would let model output
     decide what the coach calls explained. The rows are `anomalyRecords`', the
     ones a conjecture pass is shown and a run's `conjectureCandidate` counts, so
     the three cannot disagree about what does not fit. A conjecture is never the
     anchor: the press would stack one proposal on another. */
  {
    const rows = new Map<string, AnomalyRecord[]>()
    for (const r of anomalyRecords(session)) {
      rows.set(r.nodeId, [...(rows.get(r.nodeId) ?? []), r])
    }
    const conjectureBelow = (id: string): boolean =>
      descendants(id).some((d) => session.nodes[d]?.kind === 'conjecture')
    const open = pool.filter(
      (p) =>
        rows.has(p.node.id) &&
        p.node.kind !== 'conjecture' &&
        p.node.parentId !== null &&
        !conjectureBelow(p.node.id),
    )
    if (open.length) {
      let best = open[0]!
      for (const p of open) if (heavier(p, best) < 0) best = p
      const words = [...new Set(rows.get(best.node.id)!.map(anomalyWords))]
      add('unexplained', best.node, {
        body: `This claim carries a record of something that does not fit — ${words.join('; ')} — and no insight hangs anywhere beneath it. An insight pass aimed here starts from records like this one, and proposes rather than settles.`,
        metric: `${plural(rows.get(best.node.id)!.length, 'RECORD')} HERE · ${plural(open.length, 'CLAIM')} LIKE IT · NO INSIGHT BENEATH`,
      })
    }
  }

  /* 19 · unsearched — a conjecture no prior-art search has read under the wording
     it carries now, oldest first. The one silence on the board that reads as a
     claim: a proposal nobody has searched for looks, to a reader, like one nobody
     has made. */
  {
    const unsearched = pool.filter((p) => {
      const c = p.node.conjecture
      return (
        p.node.kind === 'conjecture' &&
        c !== undefined &&
        !p.node.terminal &&
        !c.priorArt?.some((a) => a.contentHash === c.registered.contentHash)
      )
    })
    if (unsearched.length) {
      let best = unsearched[0]!
      for (const p of unsearched) if (p.index < best.index) best = p
      const c = best.node.conjecture!
      add('unsearched', best.node, {
        body: c.priorArt?.length
          ? 'A prior-art search read this insight before it was rewritten, and none has read the wording it carries now. Until one does, “no source the model knows” is its model’s report on its own memory.'
          : 'Nothing has searched for whether this insight has already been said. Until something does, “no source the model knows” is its model’s report on its own memory.',
        // The word the fold's filter and an unsearched card say, and the reader's
        // day (D210): the UTC one dated a conjecture registered before breakfast
        // in Tokyo the day before its own card did.
        metric: `${plural(unsearched.length, 'INSIGHT')} ${STATUS_WORD.notSearchedForPriorArt.toUpperCase()} · REGISTERED ${calendarDay(c.registered.at)}`,
      })
    }
  }

  return out
}

/** Each record of what does not fit, as the `unexplained` body names it. A mixed
 *  check is said from its record instead, by `anomalyWords`, so this table has no
 *  row in which to keep one fixed clause for it. */
const ANOMALY_WORDS: Record<Exclude<AnomalyKind, 'mixed'>, string> = {
  'refuted-outcome': 'a result you recorded went against it',
  'refuted-source': 'a page a source check opened states the opposite',
  'panel-split': 'a panel’s three lenses did not agree on it',
  'audit-objection': 'a cross-examination objected to it',
  contradiction: 'it is an open contradiction',
  gap: 'it is an open gap in what is known',
}

/**
 * One record's words in the `unexplained` body. A mixed check's say which way its
 * sources went, in the shape the conjecture pass is shown (`mixedShape`): the
 * clause was "the sources disagreed when it was checked" under every mixed check,
 * which the record says of one shape in four (D325). No counts, as on the
 * Breakthroughs hold (D323): the card's metric counts records, and the claim's own
 * check prints the stance strip one press away.
 */
function anomalyWords(r: AnomalyRecord): string {
  if (r.kind !== 'mixed') return ANOMALY_WORDS[r.kind]
  const sided = (k: number | undefined) =>
    k === 1 ? 'the source that took a side' : 'the sources that took a side'
  switch (mixedShape(r.sides)) {
    case 'against':
      return `its source check read mixed, and ${sided(r.sides?.disputes)} disputed it`
    case 'with':
      return `its source check read mixed, though ${sided(r.sides?.supports)} supported it`
    case 'split':
      return 'its source check read mixed, and the sources that took a side disagreed with each other'
    case 'unsided':
      return 'its source check read mixed, and no source on it is recorded as taking a side'
  }
}

/**
 * The timestamp of a record on `d` that landed after `written`, or null.
 *
 * An outcome of ANY bearing counts — "supported" arriving after the reasoning
 * above it is the same bookkeeping fact as "refuted" — and a live source check
 * counts only where it disagrees, because a supporting check leaves the number
 * above it standing.
 */
function recordAfter(d: ReasonNode, written: number): number | null {
  if (d.outcome && d.outcome.recordedAt > written) return d.outcome.recordedAt
  const v = d.verification
  if (
    v &&
    verificationLive(d) &&
    (v.reading === 'refuted' || v.reading === 'mixed') &&
    v.checkedAt > written
  ) {
    return v.checkedAt
  }
  return null
}

const has = (map: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(map, key)

/**
 * Fired cues → what the board shows: muted and skipped kinds out, seen instances
 * out, one cue per anchor, sorted by the lens, eight of them.
 *
 * The per-anchor dedupe is the crowding fix: a node carrying `thin`, `blast` and
 * `strong` at once keeps its highest-risk cue and the others wait for the next
 * recompute, which is what makes eight pins mean eight places rather than four
 * places twice over. Highest RISK wins that contest under every lens, including
 * CHEAPEST — the lens orders the board, it does not decide what a node's most
 * serious problem is.
 *
 * `usdOf` is injected rather than computed here, because the price arithmetic
 * lives with the button that spends. It defaults to free, so a caller with no
 * pricing gets a stable order instead of an exception.
 */
export function assembleCues(
  all: Cue[],
  f: CueFilters,
  lens: CueLens,
  usdOf: (c: Cue) => number = () => 0,
): Cue[] {
  const live = all.filter(
    (c) => !has(f.muted, c.kind) && (f.skips[c.kind] ?? 0) < SKIPS_TO_MUTE && !has(f.seen, c.key),
  )

  const perAnchor = new Map<string, Cue>()
  for (const c of live) {
    const held = perAnchor.get(c.anchorId)
    if (!held || c.riskRank > held.riskRank) perAnchor.set(c.anchorId, c)
  }

  // What CHEAPEST actually sorts on. A free action is genuinely zero. A PAID
  // action with no figure — an audit before its auditor is priced, a link scan,
  // any model without a list rate — is UNKNOWN, and unknown is not zero: sorted
  // as free it would lead the very ordering whose button may then print a real
  // figure, which is the one disagreement the injected `usdOf` exists to
  // prevent. Unknowns go last instead, ties falling to risk like every other
  // tie in this file. A real paid projection is never zero, so zero from
  // `usdOf` on a paid action can only mean "no figure".
  const cheapKey = (c: Cue): number => {
    if (!c.action || !CUE_ACTION_PAID[c.action.kind]) return 0
    const usd = usdOf(c)
    return usd > 0 ? usd : Number.POSITIVE_INFINITY
  }

  const ranked = [...perAnchor.values()]
  ranked.sort((a, b) => {
    if (lens === 'cheapest') return cheapKey(a) - cheapKey(b) || b.riskRank - a.riskRank
    if (lens === 'experiment') return b.expRank - a.expRank || b.riskRank - a.riskRank
    return b.riskRank - a.riskRank
  })
  return ranked.slice(0, CUE_VISIBLE_MAX)
}
